/**
 * Can the player walk from `start` to `goal` on a `width`×`height` grid, stepping
 * 4-directionally and never onto a painted Empty (wall) cell? A BFS preflight for
 * the generation wizard — the same reachability the backend graph builder and the
 * story verifier enforce, computed on the raw painted grid before we generate.
 *
 * Returns false if start or goal is missing, out of bounds, a wall, or walled off.
 */
export function isStartToGoalReachable(width, height, start, goal, empties = []) {
    const cols = Math.floor(width);
    const rows = Math.floor(height);
    if (!start || !goal || cols <= 0 || rows <= 0)
        return false;
    const inBounds = (x, y) => x >= 0 && x < cols && y >= 0 && y < rows;
    if (!inBounds(start.x, start.y) || !inBounds(goal.x, goal.y))
        return false;
    const key = (x, y) => `${x},${y}`;
    const walls = new Set(empties.map(e => key(e.x, e.y)));
    // Start/goal can't be walls — if painted as one, treat it as blocked.
    if (walls.has(key(start.x, start.y)) || walls.has(key(goal.x, goal.y)))
        return false;
    const goalKey = key(goal.x, goal.y);
    const seen = new Set([key(start.x, start.y)]);
    const queue = [start];
    const deltas = [[0, -1], [1, 0], [0, 1], [-1, 0]];
    while (queue.length) {
        const cur = queue.shift();
        if (key(cur.x, cur.y) === goalKey)
            return true;
        for (const [dx, dy] of deltas) {
            const nx = cur.x + dx;
            const ny = cur.y + dy;
            const k = key(nx, ny);
            if (!inBounds(nx, ny) || seen.has(k) || walls.has(k))
                continue;
            seen.add(k);
            queue.push({ x: nx, y: ny });
        }
    }
    return false;
}
/**
 * ROUTE MODE gate: is `goal` reachable from `start` walking ONLY on the drawn route
 * cells (4-directionally)? Start and goal always count as on the route. Unlike the
 * walls check above, here the walkable set IS the route — everything else is
 * impassable background — so this confirms the author actually connected them.
 */
export function isRouteConnected(start, goal, routeCells = []) {
    if (!start || !goal)
        return false;
    const key = (x, y) => `${x},${y}`;
    const onRoute = new Set(routeCells.map(c => key(c.x, c.y)));
    onRoute.add(key(start.x, start.y));
    onRoute.add(key(goal.x, goal.y));
    const goalKey = key(goal.x, goal.y);
    const seen = new Set([key(start.x, start.y)]);
    const queue = [start];
    const deltas = [[0, -1], [1, 0], [0, 1], [-1, 0]];
    while (queue.length) {
        const cur = queue.shift();
        if (key(cur.x, cur.y) === goalKey)
            return true;
        for (const [dx, dy] of deltas) {
            const nx = cur.x + dx;
            const ny = cur.y + dy;
            const k = key(nx, ny);
            if (seen.has(k) || !onRoute.has(k))
                continue;
            seen.add(k);
            queue.push({ x: nx, y: ny });
        }
    }
    return false;
}
/**
 * ROUTE MODE integrity: how many DRAWN route cells are NOT part of the connected
 * component that links start → goal. A single 4-connected path has zero orphans;
 * a stray/disconnected segment (which the generator would silently discard as
 * background) is flagged so the author fixes it. Start/goal themselves never count
 * as orphans. Returns 0 when start or goal is missing (nothing to walk yet).
 */
export function routeOrphanCount(start, goal, routeCells = []) {
    if (!start || !goal)
        return 0;
    const key = (x, y) => `${x},${y}`;
    const onRoute = new Set(routeCells.map(c => key(c.x, c.y)));
    onRoute.add(key(start.x, start.y));
    onRoute.add(key(goal.x, goal.y));
    // BFS the connected component reachable from start over route cells.
    const seen = new Set([key(start.x, start.y)]);
    const queue = [start];
    const deltas = [[0, -1], [1, 0], [0, 1], [-1, 0]];
    while (queue.length) {
        const cur = queue.shift();
        for (const [dx, dy] of deltas) {
            const k = key(cur.x + dx, cur.y + dy);
            if (seen.has(k) || !onRoute.has(k))
                continue;
            seen.add(k);
            queue.push({ x: cur.x + dx, y: cur.y + dy });
        }
    }
    // Orphans: distinct drawn route cells the walk never reached (excluding start/goal).
    let orphans = 0;
    for (const k of onRoute) {
        if (k === key(start.x, start.y) || k === key(goal.x, goal.y))
            continue;
        if (!seen.has(k))
            orphans += 1;
    }
    return orphans;
}
